import React from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { FaWater, FaFilter, FaCheckCircle, FaTools, FaChartLine, FaShieldAlt, FaRecycle, FaIndustry, FaFlask, FaCog } from 'react-icons/fa';

const ReverseOsmosis = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={8}>
              <h1 className="display-4 fw-bold mb-4">Reverse Osmosis (RO) Plant Treatment</h1>
              <p className="lead mb-4">
                Advanced membrane filtration technology for high-purity water production, 
                membrane protection, and comprehensive RO system maintenance solutions.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Overview */}
      <section className="section-padding">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Reverse Osmosis Technology</h2>
              <p className="lead">
                RO systems use semi-permeable membranes to remove dissolved solids, contaminants, 
                and impurities from water, producing high-purity water for critical industrial applications.
              </p>
            </Col>
          </Row>
          
          <Row className="mb-4">
            <Col lg={6}>
              <h4 className="mb-3">RO System Benefits:</h4>
              <ul>
                <li>Removes up to 99% of dissolved solids</li>
                <li>Eliminates bacteria, viruses, and pathogens</li>
                <li>Reduces water hardness and scaling potential</li>
                <li>Improves product quality and process efficiency</li>
                <li>Lower operating costs compared to alternatives</li>
              </ul>
            </Col>
            <Col lg={6}>
              <h4 className="mb-3">Industrial Applications:</h4>
              <ul>
                <li>Boiler feed water treatment</li>
                <li>Pharmaceutical water production</li>
                <li>Food and beverage processing</li>
                <li>Electronics manufacturing</li>
                <li>Power generation water systems</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* RO System Types */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">RO System Configurations</h2>
              <p className="lead">Customized RO solutions for different water treatment requirements</p>
            </Col>
          </Row>
          
          <Row>
            <Col md={6} lg={4} className="mb-4">
              <Card className="h-100">
                <Card.Body className="text-center">
                  <div className="feature-icon mb-3"><FaIndustry /></div>
                  <Card.Title>Industrial RO Plants</Card.Title>
                  <Card.Text>
                    Large-scale systems for high-volume water treatment with multi-stage membrane arrays.
                  </Card.Text>
                  <Badge bg="primary">High Capacity</Badge>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4} className="mb-4">
              <Card className="h-100">
                <Card.Body className="text-center">
                  <div className="feature-icon mb-3"><FaFilter /></div>
                  <Card.Title>Commercial RO Systems</Card.Title>
                  <Card.Text>
                    Medium-capacity systems for commercial buildings and smaller industrial applications.
                  </Card.Text>
                  <Badge bg="success">Versatile</Badge>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4} className="mb-4">
              <Card className="h-100">
                <Card.Body className="text-center">
                  <div className="feature-icon mb-3"><FaRecycle /></div>
                  <Card.Title>Sea Water RO Systems</Card.Title>
                  <Card.Text>
                    Specialized systems for desalination of seawater with high-pressure membranes.
                  </Card.Text>
                  <Badge bg="info">Desalination</Badge>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Membrane Cleaners & Treatment */}
      <section className="section-padding">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold">Membrane Cleaners & Treatment Chemicals</h2>
              <p className="lead">Comprehensive membrane care solutions for optimal performance</p>
            </Col>
          </Row>
          <Row>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaFlask /></div>
                <h5>Acid Cleaners</h5>
                <p className="small">Remove mineral scale and metal deposits from membrane surfaces</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaTools /></div>
                <h5>Alkaline Cleaners</h5>
                <p className="small">Eliminate organic fouling, biofilms, and colloidal deposits</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaShieldAlt /></div>
                <h5>Antiscalants</h5>
                <p className="small">Prevent scale formation and protect membrane integrity</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaChartLine /></div>
                <h5>Biocides</h5>
                <p className="small">Control biological growth and prevent membrane biofouling</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Treatment Process */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">RO Treatment Process</h2>
              <p className="lead">Multi-stage water purification through membrane technology</p>
            </Col>
          </Row>
          
          <Row>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaFilter className="me-2" />Pre-Treatment Stage</h4>
              <ul>
                <li>Multimedia filtration for suspended solids removal</li>
                <li>Water softening to prevent membrane scaling</li>
                <li>Antiscalant dosing for scale control</li>
                <li>Cartridge filtration for fine particle removal</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaWater className="me-2" />RO Membrane Stage</h4>
              <ul>
                <li>High-pressure water pumping through membranes</li>
                <li>Separation of dissolved solids and contaminants</li>
                <li>Production of permeate (pure water) and concentrate</li>
                <li>Multi-pass configuration for higher purity</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaShieldAlt className="me-2" />Post-Treatment Stage</h4>
              <ul>
                <li>UV disinfection for microbial protection</li>
                <li>pH adjustment and remineralization</li>
                <li>Final polishing filtration</li>
                <li>Storage and distribution system protection</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaRecycle className="me-2" />Cleaning & Maintenance</h4>
              <ul>
                <li>Regular membrane cleaning schedules</li>
                <li>Performance monitoring and optimization</li>
                <li>Membrane replacement planning</li>
                <li>System sanitization procedures</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Key Features */}
      <section className="section-padding">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold">System Features & Capabilities</h2>
              <p className="lead">Advanced features for reliable operation</p>
            </Col>
          </Row>
          <Row>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaChartLine /></div>
                <h5>Smart Monitoring</h5>
                <p className="small">Real-time performance tracking with digital control systems</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaShieldAlt /></div>
                <h5>Energy Recovery</h5>
                <p className="small">Energy-efficient designs with recovery systems for lower operating costs</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaTools /></div>
                <h5>Automated Operation</h5>
                <p className="small">Fully automated systems with minimal operator intervention required</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaCheckCircle /></div>
                <h5>Quality Assurance</h5>
                <p className="small">Consistent water quality with automated quality control systems</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Applications by Industry */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Industry-Specific Applications</h2>
              <p className="lead">Tailored RO solutions for various industrial sectors</p>
            </Col>
          </Row>
          
          <Row>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaIndustry className="me-2" />Power Generation</h4>
              <ul>
                <li>Boiler feed water demineralization</li>
                <li>Closed-loop cooling systems</li>
                <li>Turbine wash water production</li>
                <li>Make-up water for cooling towers</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaFlask className="me-2" />Pharmaceutical</h4>
              <ul>
                <li>Purified water (PW) production</li>
                <li>Water for injection (WFI) systems</li>
                <li>CIP system water preparation</li>
                <li>Equipment cleaning and sanitization</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaWater className="me-2" />Food & Beverage</h4>
              <ul>
                <li>Ingredient water preparation</li>
                <li>Bottle washing and rinsing</li>
                <li>Process water for food production</li>
                <li>Steam generation for cooking</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaCog className="me-2" />Electronics</h4>
              <ul>
                <li>Ultrapure water systems</li>
                <li>Semiconductor manufacturing</li>
                <li>Circuit board cleaning</li>
                <li>Cooling water for equipment</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Technical Specifications */}
      <section className="section-padding">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Technical Specifications</h2>
              <p className="lead">System performance and design parameters</p>
            </Col>
          </Row>
          
          <Row>
            <Col lg={6}>
              <h4 className="mb-3">Performance Parameters:</h4>
              <ul>
                <li>Rejection rate: Up to 99.5% for dissolved solids</li>
                <li>Recovery rate: 50-85% depending on feed water quality</li>
                <li>Operating pressure: 5-70 bar (depending on system)</li>
                <li>Flow rates: 100 to 100,000 LPH</li>
                <li>pH range: 2-11 (membrane dependent)</li>
              </ul>
            </Col>
            <Col lg={6}>
              <h4 className="mb-3">System Components:</h4>
              <ul>
                <li>High-pressure pumps and motors</li>
                <li>Spiral wound or hollow fiber membranes</li>
                <li>Pressure vessels and piping</li>
                <li>Control panels and instrumentation</li>
                <li>Pre-treatment and post-treatment equipment</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Call to Action */}
      <section className="section-padding bg-primary text-white">
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto">
              <h2 className="display-5 fw-bold mb-4">Need RO System Solutions?</h2>
              <p className="lead mb-4">
                Contact us for comprehensive RO system design, membrane cleaning programs, and water treatment solutions.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Button variant="warning" size="lg" className="cta-button">
                  System Assessment
                </Button>
                <Button variant="outline-light" size="lg">
                  Membrane Cleaning Program
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default ReverseOsmosis;
